import type { EnemyKind, Head, OriginId, QuestState } from './types.ts';

export * from './tiles.ts';
export * from './edge.ts';
import { DRAWN_H, MAP_H, MAP_W, NORTH, T_COPPER, T_COUNTER, T_CRUST, T_DINER, T_DIRT, T_GLOW, T_GRASS, T_GREASE, T_MARROW, T_PROP, T_RIB, T_ROAD, T_ROCK, T_SALT, T_STATIC, T_STATION, T_TOXIC, T_TRACK, T_WATER, T_GLASS, T_FLOOR, TILE, solid } from './tiles.ts';
import { GEN, growWorld } from './worldgen.ts';
import { EDGE, LAST_STOP, stampPastEdge } from './edge.ts';
import { BASES } from './items.ts';

/** Grease cut, the slow south, and the marrow yard. */
export const SOUTH = 82;

/** How many seats. RADVILLE_MAX_PLAYERS changes it on the server. */
export const MAX_PLAYERS = 16;
export const START_TABS = 12;
export const QUEST_TABS = 28;
export const HUM_TABS = 12;
export const SALT_TABS = 20;
export const HISS_TABS = 22;
export const DRAW_TABS = 26;
export const BAG_MAX = 30;
export const STASH_MAX = 60;
export const PLAYER_RADIUS = 5;
export const TALK_R = 46;
export const SHOP_R = 78;
export const USE_R = 42;
export const MELEE_ARC = 0.9;

export const SKINS = ['#e2b48a', '#c4845a', '#8a5a3c', '#d9d3c2', '#b5c46a', '#7fbfa3', '#d08b7a'];
export const CLOTHS = ['#a34532', '#3c5e74', '#6d5432', '#3f4f34', '#7a4b6e', '#c4a15a'];
export const ACCENTS = ['#e0b15a', '#efeae0', '#7fbfb2', '#d4543a'];

export const HEAD_LABELS: Record<Head, string> = {
  bald: 'Bald',
  tuft: 'Tuft',
  antenna: 'Antenna',
  hood: 'Hood',
  mohawk: 'Mohawk',
};

export const ORIGINS: { id: OriginId; name: string; blurb: string }[] = [
  { id: 'knuckle', name: 'Extra Knuckle', blurb: 'One more than the argument required. Melee hits harder.' },
  { id: 'carapace', name: 'Tin Carapace', blurb: 'A rash that decided to be armor. You soak a little.' },
  { id: 'lamp', name: 'Lamp Eye', blurb: 'It waters in the sun and lights fuses. Ranged hits harder.' },
  { id: 'jackleg', name: 'Jackleg', blurb: 'The left one is late, which somehow makes you faster.' },
];

export function isOrigin(value: unknown): value is OriginId {
  return ORIGINS.some((origin) => origin.id === value);
}

const town = (ty: number) => ty + NORTH;

export const SPAWN = { x: 18 * TILE + 8, y: town(12) * TILE + 8 };

export type NpcId = 'brine' | 'peck' | 'vetch' | 'moss' | 'sully' | 'ora' | 'tuck' | 'lastbrine';

export const NPCS: Record<NpcId, { id: NpcId; name: string; x: number; y: number }> = {
  brine: { id: 'brine', name: 'Brine', x: 18 * TILE + 8, y: town(8) * TILE + 8 },
  peck: { id: 'peck', name: 'Peck', x: 21 * TILE + 8, y: town(7) * TILE + 8 },
  vetch: { id: 'vetch', name: 'Vetch', x: 260 * TILE + 8, y: 28 * TILE + 8 },
  moss: { id: 'moss', name: 'Moss', x: 266 * TILE + 8, y: 27 * TILE + 8 },
  sully: { id: 'sully', name: 'Sully', x: 267 * TILE + 8, y: 203 * TILE + 8 },
  ora: { id: 'ora', name: 'Ora', x: 290 * TILE + 8, y: 203 * TILE + 8 },
  tuck: { id: 'tuck', name: 'Tuck', x: 147 * TILE + 8, y: 26 * TILE + 8 },
  /** Brine, at the end of the line. Only people who rode with her can get here. */
  lastbrine: { id: 'lastbrine', name: 'Brine', x: 167 * TILE + 8, y: 327 * TILE + 8 },
};

/** Stairs between the Click Station and the Undertrack. Open once the story finds them. */
export const STAIRS = {
  down: { x: 54 * TILE + 8, y: town(7) * TILE + 8 },
  downArrive: { x: 9 * TILE + 8, y: 267 * TILE + 8 },
  up: { x: 7 * TILE + 8, y: 265 * TILE + 8 },
  upArrive: { x: 53 * TILE + 8, y: town(8) * TILE + 8 },
};

export function isNpc(value: unknown): value is NpcId {
  return typeof value === 'string' && value in NPCS;
}

/** Towns. Each has a locker (they are all the same locker; the coach carries it) and a coach post. */
export type TownId = 'radville' | 'halt' | 'sump';
export const TOWNS: { id: TownId; name: string; mark: string; board: NpcId; locker: { x: number; y: number }; post: { x: number; y: number }; arrive: { x: number; y: number } }[] = [
  {
    id: 'radville',
    name: 'Radville',
    mark: 'diner',
    board: 'brine',
    locker: { x: 5 * TILE + 8, y: town(8) * TILE + 8 },
    post: { x: 25 * TILE + 8, y: town(13) * TILE + 8 },
    arrive: { x: 23 * TILE + 8, y: town(14) * TILE + 8 },
  },
  {
    id: 'halt',
    name: 'Halt',
    mark: 'halt',
    board: 'vetch',
    locker: { x: 257 * TILE + 8, y: 34 * TILE + 8 },
    post: { x: 270 * TILE + 8, y: 34 * TILE + 8 },
    arrive: { x: 268 * TILE + 8, y: 32 * TILE + 8 },
  },
  {
    id: 'sump',
    name: 'Sump',
    mark: 'sump',
    board: 'ora',
    locker: { x: 265 * TILE + 8, y: 215 * TILE + 8 },
    post: { x: 285 * TILE + 8, y: 216 * TILE + 8 },
    arrive: { x: 283 * TILE + 8, y: 214 * TILE + 8 },
  },
];

/** What the coach costs between two towns. */
export function coachFare(from: TownId, to: TownId): number {
  const order: TownId[] = ['radville', 'halt', 'sump'];
  return 6 * Math.max(1, Math.abs(order.indexOf(from) - order.indexOf(to)));
}

/** Your locker, against the diner's west wall. It does not walk. */
export const LOCKER = { x: 5 * TILE + 8, y: town(8) * TILE + 8 };
/** The cushion in the dead coach, on the road to Halt. */
export const COACH = { x: 232 * TILE + 8, y: 30 * TILE + 8 };
export const HALT_TABS = 16;

export const COOLER = { x: 51 * TILE + 8, y: town(9) * TILE + 8 };
export const WELL = { x: 25 * TILE + 8, y: town(1) * TILE + 8 };
export const CLAPPER = { x: 23 * TILE + 8, y: 6 * TILE + 8 };
export const KEEL = { x: 88 * TILE + 8, y: 28 * TILE + 8 };
export const HISS = { x: 204 * TILE + 8, y: 30 * TILE + 8 };
export const TOOTH = { x: 26 * TILE + 8, y: 121 * TILE + 8 };

export type EnemyStats = {
  name: string;
  /** The tier it belongs to. Found deeper than this, it is tougher. */
  tier: number;
  maxHp: number;
  dmg: number;
  speed: number;
  aggro: number;
  leash: number;
  melee: number;
  radius: number;
  cd: number;
  spit?: { speed: number; range: number; windup: number; fan?: number };
  /** Moves in fits instead of a line. */
  flit?: boolean;
  boss?: boolean;
  /** The relic it keeps. */
  relic?: string;
};

export const ENEMY_KINDS: Record<EnemyKind, EnemyStats> = {
  roach: { name: 'Ash Roach', tier: 1, maxHp: 18, dmg: 7, speed: 62, aggro: 104, leash: 190, melee: 20, radius: 6, cd: 0.85 },
  fastroach: { name: 'Wick Roach', tier: 1, maxHp: 14, dmg: 6, speed: 102, aggro: 120, leash: 210, melee: 18, radius: 6, cd: 0.7 },
  toad: { name: 'Pump Toad', tier: 1, maxHp: 40, dmg: 9, speed: 38, aggro: 128, leash: 180, melee: 22, radius: 8, cd: 1.45, spit: { speed: 150, range: 168, windup: 0.42 } },
  newt: { name: 'Gutter Newt', tier: 1, maxHp: 12, dmg: 5, speed: 92, aggro: 110, leash: 150, melee: 16, radius: 5, cd: 0.7 },
  slug: { name: 'Choir Slug', tier: 1, maxHp: 34, dmg: 8, speed: 26, aggro: 120, leash: 140, melee: 18, radius: 8, cd: 1.6, spit: { speed: 110, range: 130, windup: 0.5 } },
  panback: { name: 'Panback', tier: 2, maxHp: 46, dmg: 12, speed: 34, aggro: 130, leash: 170, melee: 20, radius: 9, cd: 1.3, spit: { speed: 120, range: 150, windup: 0.55 } },
  skip: { name: 'Salt Skip', tier: 2, maxHp: 10, dmg: 4, speed: 108, aggro: 110, leash: 150, melee: 14, radius: 4, cd: 0.55 },
  filament: { name: 'Filament', tier: 1, maxHp: 16, dmg: 8, speed: 78, aggro: 100, leash: 140, melee: 18, radius: 4, cd: 0.8 },
  slick: { name: 'Slick', tier: 1, maxHp: 32, dmg: 7, speed: 40, aggro: 110, leash: 150, melee: 18, radius: 8, cd: 1.1 },
  drifter: { name: 'Mile Drifter', tier: 2, maxHp: 22, dmg: 7, speed: 54, aggro: 100, leash: 220, melee: 18, radius: 6, cd: 0.9 },
  sleeper: { name: 'Road Sleeper', tier: 2, maxHp: 16, dmg: 5, speed: 36, aggro: 64, leash: 170, melee: 16, radius: 6, cd: 1.1 },
  mite: { name: 'Static Mite', tier: 3, maxHp: 12, dmg: 6, speed: 96, aggro: 110, leash: 150, melee: 14, radius: 4, cd: 0.6 },
  nail: { name: 'Choir Nail', tier: 3, maxHp: 24, dmg: 8, speed: 32, aggro: 130, leash: 160, melee: 18, radius: 5, cd: 1.2, spit: { speed: 160, range: 140, windup: 0.4 } },
  mast: { name: 'Masthead', tier: 3, maxHp: 90, dmg: 11, speed: 28, aggro: 150, leash: 190, melee: 22, radius: 10, cd: 1.5, spit: { speed: 140, range: 170, windup: 0.6, fan: 3 }, boss: true, relic: 'mastbolt' },
  tick: { name: 'Rib Tick', tier: 3, maxHp: 14, dmg: 7, speed: 88, aggro: 100, leash: 140, melee: 14, radius: 4, cd: 0.65 },
  crab: { name: 'Drawer Crab', tier: 3, maxHp: 38, dmg: 10, speed: 30, aggro: 110, leash: 150, melee: 20, radius: 8, cd: 1.15 },
  drawer: { name: 'Warm Drawer', tier: 3, maxHp: 100, dmg: 12, speed: 24, aggro: 140, leash: 180, melee: 22, radius: 11, cd: 1.7, spit: { speed: 120, range: 150, windup: 0.7, fan: 3 }, boss: true, relic: 'drawerlid' },
  leaker: { name: 'Leaker', tier: 5, maxHp: 58, dmg: 12, speed: 46, aggro: 130, leash: 180, melee: 20, radius: 8, cd: 0.95 },
  count: { name: 'The Count', tier: 5, maxHp: 140, dmg: 14, speed: 26, aggro: 170, leash: 210, melee: 24, radius: 11, cd: 1.35, spit: { speed: 150, range: 170, windup: 0.5, fan: 3 }, boss: true, relic: 'countface' },
  hound: { name: 'Scrub Dog', tier: 3, maxHp: 26, dmg: 8, speed: 96, aggro: 140, leash: 230, melee: 18, radius: 6, cd: 0.75 },
  walker: { name: 'Tin Walker', tier: 4, maxHp: 44, dmg: 11, speed: 40, aggro: 150, leash: 200, melee: 20, radius: 7, cd: 1.4, spit: { speed: 170, range: 180, windup: 0.5 } },
  moth: { name: 'Glass Moth', tier: 6, maxHp: 30, dmg: 13, speed: 88, aggro: 140, leash: 200, melee: 16, radius: 5, cd: 0.7, flit: true },
  clerk: { name: 'Dark Clerk', tier: 7, maxHp: 60, dmg: 16, speed: 38, aggro: 160, leash: 200, melee: 18, radius: 7, cd: 1.3, spit: { speed: 190, range: 190, windup: 0.45 } },
  kettle: { name: 'The Kettle', tier: 6, maxHp: 420, dmg: 20, speed: 24, aggro: 190, leash: 240, melee: 26, radius: 13, cd: 1.3, spit: { speed: 140, range: 190, windup: 0.6, fan: 5 }, boss: true, relic: 'kettlecrown' },
  master: { name: 'The Stationmaster', tier: 8, maxHp: 700, dmg: 26, speed: 30, aggro: 200, leash: 260, melee: 26, radius: 12, cd: 1.1, spit: { speed: 200, range: 210, windup: 0.5, fan: 3 }, boss: true, relic: 'lastlamp' },
  foreman: { name: 'The Foreman', tier: 6, maxHp: 380, dmg: 18, speed: 34, aggro: 160, leash: 200, melee: 24, radius: 11, cd: 1.1, spit: { speed: 170, range: 170, windup: 0.5, fan: 3 }, boss: true, relic: 'spanner' },
  blot: { name: 'Blot', tier: 8, maxHp: 64, dmg: 17, speed: 60, aggro: 150, leash: 210, melee: 17, radius: 7, cd: 0.95, flit: true },
  surveyor: { name: 'The Surveyor', tier: 8, maxHp: 760, dmg: 24, speed: 32, aggro: 180, leash: 240, melee: 30, radius: 12, cd: 1.15, spit: { speed: 210, range: 230, windup: 0.5, fan: 3 }, boss: true, relic: 'chain' },
  mainspring: { name: 'The Mainspring', tier: 8, maxHp: 820, dmg: 26, speed: 22, aggro: 170, leash: 200, melee: 28, radius: 13, cd: 1.0, spit: { speed: 170, range: 190, windup: 0.45, fan: 6 }, boss: true, relic: 'spring' },
  lastnumber: { name: 'The Last Number', tier: 10, maxHp: 1400, dmg: 34, speed: 30, aggro: 200, leash: 260, melee: 30, radius: 13, cd: 1.0, spit: { speed: 200, range: 240, windup: 0.5, fan: 5 }, boss: true, relic: 'nought' },
  wanderer: { name: 'The Wanderer', tier: 6, maxHp: 520, dmg: 18, speed: 34, aggro: 170, leash: 99999, melee: 26, radius: 12, cd: 1.2, spit: { speed: 150, range: 180, windup: 0.55, fan: 4 }, boss: true, relic: 'wanderer' },
};

/** roam is how far, in tiles, it ambles from home when nobody is close. gun means this one picked up a player weapon. */
export const ENEMY_SPAWNS: { kind: EnemyKind; tx: number; ty: number; roam?: number; gun?: 'stun' | 'laser' | 'pop' | 'bell' | 'suck'; name?: string }[] = [
  { kind: 'roach', tx: 48, ty: town(22) },
  { kind: 'roach', tx: 52, ty: town(24) },
  { kind: 'fastroach', tx: 55, ty: town(21) },
  { kind: 'roach', tx: 49, ty: town(11) },
  { kind: 'toad', tx: 54, ty: town(10) },
  { kind: 'newt', tx: 21, ty: 8 },
  { kind: 'newt', tx: 22, ty: 4 },
  { kind: 'slug', tx: 26, ty: 6 },
  { kind: 'panback', tx: 84, ty: 36 },
  { kind: 'panback', tx: 92, ty: 34 },
  { kind: 'panback', tx: 90, ty: 26 },
  { kind: 'panback', tx: 80, ty: 26 },
  { kind: 'skip', tx: 78, ty: 30 },
  { kind: 'skip', tx: 86, ty: 31 },
  { kind: 'skip', tx: 94, ty: 38 },
  { kind: 'skip', tx: 82, ty: 38 },
  { kind: 'filament', tx: 5, ty: 4 },
  { kind: 'filament', tx: 10, ty: 7 },
  { kind: 'filament', tx: 8, ty: 9 },
  { kind: 'slick', tx: 12, ty: 56 },
  { kind: 'slick', tx: 20, ty: 58 },
  { kind: 'slick', tx: 16, ty: 62 },
  { kind: 'slick', tx: 24, ty: 54 },
  // The mile. Far enough apart that the walk stays a walk.
  { kind: 'drifter', tx: 112, ty: 31, roam: 7 },
  { kind: 'drifter', tx: 136, ty: 30, roam: 7, gun: 'stun', name: 'Prod Drifter' },
  { kind: 'drifter', tx: 160, ty: 31, roam: 7 },
  { kind: 'drifter', tx: 170, ty: 31, roam: 6 },
  // Open country. Sparse on purpose.
  { kind: 'drifter', tx: 124, ty: 16, roam: 8 },
  { kind: 'drifter', tx: 156, ty: 12, roam: 8 },
  { kind: 'sleeper', tx: 78, ty: 64, roam: 8 },
  { kind: 'sleeper', tx: 108, ty: 88, roam: 8 },
  { kind: 'sleeper', tx: 146, ty: 72, roam: 8 },
  { kind: 'sleeper', tx: 90, ty: 104, roam: 7 },
  // Antenna court. They stay in the room. The mites only skitter.
  { kind: 'mite', tx: 194, ty: 24, roam: 3 },
  { kind: 'mite', tx: 208, ty: 24, roam: 3 },
  { kind: 'mite', tx: 194, ty: 40, roam: 3 },
  { kind: 'mite', tx: 208, ty: 40, roam: 3 },
  { kind: 'nail', tx: 200, ty: 24, roam: 2 },
  { kind: 'nail', tx: 206, ty: 36, roam: 2, gun: 'laser', name: 'Lamp Nail' },
  { kind: 'nail', tx: 192, ty: 36, roam: 2 },
  { kind: 'mast', tx: 200, ty: 34 },
  // The slow south.
  { kind: 'sleeper', tx: 16, ty: 78, roam: 7 },
  { kind: 'sleeper', tx: 17, ty: 92, roam: 7 },
  { kind: 'sleeper', tx: 16, ty: 100, roam: 6 },
  // Marrow yard.
  { kind: 'tick', tx: 10, ty: 118, roam: 3 },
  { kind: 'tick', tx: 32, ty: 118, roam: 3 },
  { kind: 'tick', tx: 10, ty: 126, roam: 3 },
  { kind: 'tick', tx: 32, ty: 126, roam: 3 },
  { kind: 'crab', tx: 14, ty: 124, roam: 2, gun: 'pop', name: 'Pop Crab' },
  { kind: 'crab', tx: 28, ty: 120, roam: 2 },
  { kind: 'crab', tx: 22, ty: 126, roam: 2 },
  { kind: 'drawer', tx: 18, ty: 122 },
  // The back lot, the long way around.
  { kind: 'sleeper', tx: 196, ty: 58, roam: 7 },
  { kind: 'sleeper', tx: 197, ty: 80, roam: 7 },
  { kind: 'sleeper', tx: 196, ty: 100, roam: 7 },
  { kind: 'sleeper', tx: 60, ty: 118, roam: 8 },
  { kind: 'sleeper', tx: 90, ty: 119, roam: 8 },
  { kind: 'sleeper', tx: 120, ty: 118, roam: 8, gun: 'bell', name: 'Bell Sleeper' },
  { kind: 'sleeper', tx: 150, ty: 117, roam: 8 },
  { kind: 'sleeper', tx: 178, ty: 119, roam: 7 },
  // The quiet between the court and Halt.
  { kind: 'sleeper', tx: 222, ty: 31, roam: 6, gun: 'suck', name: 'Suck Sleeper' },
  { kind: 'sleeper', tx: 244, ty: 30, roam: 6 },
  // The Leak. Farther, meaner, and they leave slag.
  { kind: 'leaker', tx: 320, ty: 26, roam: 3 },
  { kind: 'leaker', tx: 360, ty: 26, gun: 'laser', name: 'Lamp Leaker' },
  { kind: 'leaker', tx: 332, ty: 44, gun: 'pop', name: 'Pop Leaker' },
  { kind: 'leaker', tx: 348, ty: 42, roam: 2 },
  { kind: 'count', tx: 350, ty: 36 },
];

export type DecalKind =
  | 'billboard'
  | 'eat'
  | 'junk'
  | 'station'
  | 'well'
  | 'hum'
  | 'pump'
  | 'wreck'
  | 'cooler'
  | 'crate'
  | 'clapper'
  | 'pan'
  | 'keel'
  | 'lamps'
  | 'cut'
  | 'mile'
  | 'court'
  | 'slow'
  | 'marrow'
  | 'lot'
  | 'hiss'
  | 'tooth'
  | 'mast'
  | 'halt'
  | 'cushion'
  | 'leak'
  | 'cabinet';

/** x,y is the top-left of the tile or sign board. Props are drawn up from the tile. */
export const DECALS: { kind: DecalKind; x: number; y: number }[] = [
  { kind: 'billboard', x: 4 * TILE, y: town(2) * TILE },
  { kind: 'eat', x: 5 * TILE, y: town(3) * TILE },
  { kind: 'junk', x: 19 * TILE, y: town(4) * TILE },
  { kind: 'station', x: 26 * TILE, y: town(16) * TILE },
  { kind: 'well', x: 24 * TILE, y: town(2) * TILE },
  { kind: 'hum', x: 14 * TILE, y: 12 * TILE },
  { kind: 'pump', x: 49 * TILE, y: town(19) * TILE },
  { kind: 'pump', x: 53 * TILE, y: town(19) * TILE },
  { kind: 'wreck', x: 34 * TILE, y: town(20) * TILE },
  { kind: 'cooler', x: 51 * TILE, y: town(9) * TILE },
  { kind: 'crate', x: 48 * TILE, y: town(10) * TILE },
  { kind: 'clapper', x: 23 * TILE, y: 6 * TILE },
  { kind: 'pan', x: 64 * TILE, y: 28 * TILE },
  { kind: 'keel', x: 88 * TILE, y: 28 * TILE },
  { kind: 'lamps', x: 4 * TILE, y: 13 * TILE },
  { kind: 'cut', x: 14 * TILE, y: 44 * TILE },
  { kind: 'mile', x: 104 * TILE, y: 27 * TILE },
  { kind: 'court', x: 176 * TILE, y: 27 * TILE },
  { kind: 'slow', x: 10 * TILE, y: 66 * TILE },
  { kind: 'marrow', x: 8 * TILE, y: 108 * TILE },
  { kind: 'lot', x: 120 * TILE, y: 114 * TILE },
  { kind: 'wreck', x: 128 * TILE, y: 25 * TILE },
  { kind: 'wreck', x: 80 * TILE, y: 113 * TILE },
  { kind: 'mast', x: 198 * TILE, y: 28 * TILE },
  { kind: 'hiss', x: 204 * TILE, y: 30 * TILE },
  { kind: 'tooth', x: 26 * TILE, y: 121 * TILE },
  { kind: 'halt', x: 258 * TILE, y: 24 * TILE },
  { kind: 'cushion', x: 232 * TILE, y: 30 * TILE },
  { kind: 'leak', x: 300 * TILE, y: 27 * TILE },
  { kind: 'cabinet', x: 5 * TILE, y: town(8) * TILE },
];

function buildTiles(): number[][] {
  const m = Array.from({ length: MAP_H }, () => Array<number>(MAP_W).fill(T_GRASS));
  const fill = (x: number, y: number, w: number, h: number, tile: number) => {
    for (let ty = y; ty < y + h; ty++) {
      for (let tx = x; tx < x + w; tx++) {
        if (ty >= 0 && tx >= 0 && ty < MAP_H && tx < MAP_W) m[ty][tx] = tile;
      }
    }
  };
  const hollow = (x: number, y: number, w: number, h: number, wall: number, floor: number) => {
    fill(x, y, w, h, wall);
    fill(x + 1, y + 1, w - 2, h - 2, floor);
  };
  const S = NORTH;

  fill(2, 14 + S, 28, 14, T_DIRT);
  fill(17, 5 + S, 10, 8, T_DIRT);
  fill(16, 13 + S, 10, 3, T_DIRT);
  fill(22, 18 + S, 24, 4, T_ROAD);
  fill(44, 16 + S, 14, 12, T_ROAD);
  fill(46, 15 + S, 12, 1, T_ROAD);

  hollow(3, 3 + S, 14, 10, T_DINER, T_DIRT);
  hollow(46, 5 + S, 12, 10, T_STATION, T_DIRT);
  fill(19, 5 + S, 5, 1, T_COUNTER);

  fill(30, 25 + S, 5, 3, T_WATER);
  fill(24, S, 3, 2, T_WATER);
  fill(38, 12 + S, 3, 2, T_ROCK);
  fill(8, 30 + S, 2, 2, T_ROCK);

  fill(34, 20 + S, 3, 2, T_PROP);
  m[19 + S][49] = T_PROP;
  m[19 + S][53] = T_PROP;
  m[10 + S][48] = T_PROP;
  m[9 + S][51] = T_PROP;

  m[18 + S][46] = T_STATION;
  m[18 + S][55] = T_STATION;
  m[25 + S][46] = T_STATION;
  m[25 + S][55] = T_STATION;

  // The bowl north of the well. Door faces south, toward town.
  hollow(18, 1, 16, 11, T_ROCK, T_DIRT);
  fill(28, 4, 4, 4, T_WATER);
  fill(20, 12, 8, 2, T_DIRT);
  fill(20, 14, 4, 7, T_DIRT);
  m[11][24] = T_DIRT;
  m[11][25] = T_DIRT;

  // Doors last so later fills cannot reseal them.
  m[7 + S][16] = T_DIRT;
  m[8 + S][16] = T_DIRT;
  m[8 + S][5] = T_PROP;
  m[14 + S][50] = T_ROAD;
  m[14 + S][51] = T_ROAD;

  // East of the station the dirt gives up. The pan is a white bowl with a west door.
  fill(58, 28, 16, 7, T_SALT);
  hollow(74, 22, 26, 20, T_CRUST, T_SALT);
  m[32][74] = T_SALT;
  m[33][74] = T_SALT;
  m[24][86] = T_CRUST;
  m[36][80] = T_CRUST;
  m[30][94] = T_CRUST;

  // West of the bowl, the lamps grew back. A two-tile alley runs down the diner's west side.
  fill(2, 1, 13, 11, T_GLASS);
  fill(1, 12, 2, 18, T_DIRT);
  m[3][4] = T_ROCK;
  m[5][12] = T_ROCK;
  m[6][7] = T_ROCK;

  // South of the square the ground went soft.
  fill(12, 42, 8, 8, T_DIRT);
  hollow(6, 50, 24, 16, T_ROCK, T_GREASE);
  m[50][15] = T_GREASE;
  m[50][16] = T_GREASE;
  m[58][10] = T_ROCK;
  m[60][24] = T_ROCK;

  // East of the pan, a long dull mile, then the antenna court.
  fill(100, 29, 84, 4, T_TRACK);
  // A platform halfway down the mile. Nobody is meant to get off here.
  fill(141, 25, 13, 4, T_FLOOR);
  m[29][112] = T_ROCK;
  m[32][140] = T_ROCK;
  m[29][168] = T_ROCK;
  fill(128, 25, 3, 2, T_PROP);
  hollow(184, 20, 30, 24, T_COPPER, T_STATIC);
  m[24][190] = T_COPPER;
  m[24][206] = T_COPPER;
  m[38][190] = T_COPPER;
  m[38][208] = T_COPPER;
  m[26][200] = T_COPPER;
  m[28][198] = T_COPPER;
  m[28][199] = T_COPPER;
  m[29][198] = T_COPPER;
  m[29][199] = T_COPPER;

  // South of the cut, another quiet, then the marrow yard.
  fill(14, 66, 6, 46, T_TRACK);
  m[78][14] = T_ROCK;
  m[94][19] = T_ROCK;
  fill(14, 84, 2, 2, T_WATER);
  hollow(4, 112, 34, 18, T_RIB, T_MARROW);
  m[116][10] = T_RIB;
  m[116][28] = T_RIB;
  m[126][12] = T_RIB;
  m[124][30] = T_RIB;

  // The back lot joins the court to the yard the long way.
  fill(194, 44, 6, 74, T_TRACK);
  fill(38, 116, 162, 6, T_TRACK);
  fill(80, 113, 3, 2, T_PROP);
  fill(90, 8, 8, 5, T_ROCK);
  fill(120, 48, 6, 10, T_ROCK);
  fill(60, 78, 10, 4, T_ROCK);
  fill(100, 60, 4, 3, T_WATER);

  m[30][99] = T_SALT;
  m[31][99] = T_SALT;
  m[30][184] = T_STATIC;
  m[31][184] = T_STATIC;
  m[43][196] = T_STATIC;
  m[43][197] = T_STATIC;
  m[65][16] = T_GREASE;
  m[65][17] = T_GREASE;
  m[112][16] = T_MARROW;
  m[112][17] = T_MARROW;
  m[118][37] = T_MARROW;
  m[119][37] = T_MARROW;

  // East of the court, one more quiet, then Halt. A second sitting.
  fill(214, 29, 40, 4, T_TRACK);
  hollow(254, 22, 20, 16, T_ROCK, T_DIRT);
  m[30][213] = T_STATIC;
  m[31][213] = T_STATIC;
  m[30][254] = T_DIRT;
  m[31][254] = T_DIRT;

  // East of Halt. The edges of the track start to shine, then the Leak.
  fill(274, 29, 36, 4, T_TRACK);
  m[29][292] = T_GLOW;
  m[29][293] = T_GLOW;
  m[32][292] = T_GLOW;
  m[32][293] = T_GLOW;
  fill(304, 34, 3, 2, T_TOXIC);
  hollow(310, 18, 62, 32, T_ROCK, T_GLOW);
  m[30][273] = T_DIRT;
  m[31][273] = T_DIRT;
  m[30][310] = T_GLOW;
  m[31][310] = T_GLOW;
  fill(312, 29, 50, 4, T_TRACK);
  fill(324, 24, 5, 4, T_TOXIC);
  fill(356, 22, 4, 3, T_TOXIC);
  fill(336, 40, 4, 3, T_TOXIC);
  m[24][318] = T_ROCK;
  m[22][344] = T_ROCK;
  m[44][366] = T_ROCK;

  const open: [string, number, number][] = [
    ['diner door', 16, 7 + S],
    ['diner door', 16, 8 + S],
    ['station door', 50, 14 + S],
    ['station door', 51, 14 + S],
    ['brine', 18, 8 + S],
    ['peck', 21, 7 + S],
    ['spawn', 18, 12 + S],
    ['cooler side', 50, 9 + S],
    ['cistern door', 24, 11],
    ['cistern door', 25, 11],
    ['clapper', 23, 6],
    ['pan door', 74, 32],
    ['pan door', 74, 33],
    ['keel', 88, 28],
    ['lamp row', 8, 6],
    ['cut door', 15, 50],
    ['cut door', 16, 50],
    ['slick yard', 16, 56],
    ['pan back door', 99, 30],
    ['pan back door', 99, 31],
    ['mile', 136, 30],
    ['court door', 184, 30],
    ['court door', 184, 31],
    ['hiss', 204, 30],
    ['mast', 200, 34],
    ['court south', 196, 43],
    ['slow', 16, 88],
    ['cut south', 16, 65],
    ['marrow door', 16, 112],
    ['tooth', 26, 121],
    ['drawer', 18, 122],
    ['yard east', 37, 118],
    ['back lot', 120, 118],
    ['lot south', 196, 80],
    ['court east', 213, 30],
    ['coach', 232, 30],
    ['halt door', 254, 30],
    ['vetch', 260, 28],
    ['moss', 266, 27],
    ['halt east', 273, 30],
    ['leak door', 310, 30],
    ['leak path', 328, 30],
    ['count', 350, 36],
    ['lead', 352, 38],
  ];
  const keep: [number, number][] = [
    ...open.map(([, tx, ty]) => [tx, ty] as [number, number]),
    ...ENEMY_SPAWNS.map((spawn) => [spawn.tx, spawn.ty] as [number, number]),
    ...[...NODES, ...BOXES, ...MARKS].map((spot) => [Math.floor(spot.x / TILE), Math.floor(spot.y / TILE)] as [number, number]),
  ];
  // Places past the drawn country are stamped afterwards; the seed never hears of them.
  growWorld(
    m,
    18,
    12 + S,
    keep.filter(([, ty]) => ty < DRAWN_H),
  );
  GEN.props.push({ kind: 'sign', x: 143 * TILE, y: 22 * TILE, text: 'STOP 2 AND A HALF' });
  GEN.props.push({ kind: 'bench', x: 144 * TILE + 8, y: 26 * TILE + 8 });
  GEN.props.push({ kind: 'bench', x: 151 * TILE + 8, y: 26 * TILE + 8 });
  GEN.props.push({ kind: 'lamppost', x: 142 * TILE + 8, y: 28 * TILE + 4 });
  stampPastEdge(m);
  for (const town of TOWNS) {
    open.push([`${town.id} post`, Math.floor(town.post.x / TILE), Math.floor(town.post.y / TILE)]);
    open.push([`${town.id} arrival`, Math.floor(town.arrive.x / TILE), Math.floor(town.arrive.y / TILE)]);
  }
  for (const npc of Object.values(NPCS)) open.push([npc.name, Math.floor(npc.x / TILE), Math.floor(npc.y / TILE)]);
  for (const [label, tx, ty] of open) {
    if (solid(m[ty][tx])) throw new Error(`Radville map: ${label} is walled in at ${tx},${ty}`);
  }
  for (const spawn of ENEMY_SPAWNS) {
    if (solid(m[spawn.ty][spawn.tx])) {
      throw new Error(`Radville map: ${spawn.kind} spawn is walled in at ${spawn.tx},${spawn.ty}`);
    }
  }
  return m;
}


export type Dialogue = { text: string; choices: { id: string; label: string }[]; shop?: boolean };

export function brineLines(quest: QuestState): Dialogue {
  switch (quest) {
    case 'new':
      return {
        text: 'Brine blinks out of order. "Radville is the diner and the dirt around it. Something in the Click Station stole the cooler cell. East, past the dead car. Bring it back and I\'ll pay in tabs."',
        choices: [
          { id: 'accept', label: "I'll bring the cell back." },
          { id: 'leave', label: 'Not yet.' },
        ],
      };
    case 'accepted':
      return {
        text: '"Still east. Past the car that gave up. The cell glows the color of a bad idea. If your tabs jingle, Peck sells things with grips."',
        choices: [{ id: 'leave', label: 'Alright.' }],
      };
    case 'carrying':
      return {
        text: '"That\'s it. I can hear it being pleased with itself."',
        choices: [
          { id: 'turnin', label: 'Here. Pay me.' },
          { id: 'leave', label: "I'm still holding it." },
        ],
      };
    case 'done':
      return {
        text: 'Brine squints with the spare eye. "Tabs are yours. The well north of the square is louder now. Something climbed into the bowl and started keeping time. Bring me the clapper. Don\'t drink on the way."',
        choices: [
          { id: 'hum', label: "I'll get the clapper." },
          { id: 'leave', label: 'Not tonight.' },
        ],
      };
    case 'hum':
      return {
        text: '"North, past the well. Stone bowl, ticking middle. Newts in the wet, and something slower that sings."',
        choices: [{ id: 'leave', label: 'I hear it.' }],
      };
    case 'hum_got':
      return {
        text: '"That is a terrible little bell. I\'ll take it. You can keep the stick it was married to."',
        choices: [
          { id: 'humturn', label: 'Pay me.' },
          { id: 'leave', label: "I'm still holding it." },
        ],
      };
    case 'hum_done':
      return {
        text: 'Brine listens past the door. "The well shut up. East of the station the ground went white. Something out there is knitting a keel. Bring it back. Do not lick your fingers."',
        choices: [
          { id: 'salt', label: "I'll bring the keel." },
          { id: 'leave', label: 'Not in this heat.' },
        ],
      };
    case 'salt':
      return {
        text: '"Past the pumps, keep east until the dirt gives up. The pan is bright. The things in it think they are the ground."',
        choices: [{ id: 'leave', label: 'I can squint.' }],
      };
    case 'salt_got':
      return {
        text: '"That is a mean little spar. I\'ll take the itch. You can keep the sharp end."',
        choices: [
          { id: 'saltturn', label: 'Pay me.' },
          { id: 'leave', label: "I'm still holding it." },
        ],
      };
    case 'salt_done':
      return {
        text: '"The pan can keep growing. You have the part that cuts. Lamps are still west of the bowl, and the ground is still soft south of the square. If you are bored of both, the pan grew a back door. East of the white it goes quiet for a while. Then the metal sings. Bring me the hiss. It is a prong with opinions."',
        choices: [
          { id: 'hiss', label: "I'll walk the mile." },
          { id: 'leave', label: 'Not in this quiet.' },
        ],
      };
    case 'hiss':
      return {
        text: '"Out the back of the pan. Stay on the dull ground. The things on the mile wander, so you can walk around them. The court is where the dishes face. The hiss is under the mast. Do not lick the dishes."',
        choices: [{ id: 'leave', label: 'I can be quiet.' }],
      };
    case 'hiss_got':
      return {
        text: '"I can hear it in your pocket. It is trying to conduct you."',
        choices: [
          { id: 'hissturn', label: 'Pay me.' },
          { id: 'leave', label: "I'm still holding it." },
        ],
      };
    case 'hiss_done':
      return {
        text: '"Good. Your teeth can stop ringing. South of the grease cut there is another quiet, longer if you take the back lot, and past the short quiet the ground learned ribs. Something down there is a drawer that stayed warm. Bring the tooth. If it bites, that is the tooth agreeing."',
        choices: [
          { id: 'drawer', label: "I'll go south." },
          { id: 'leave', label: 'The quiet can wait.' },
        ],
      };
    case 'drawer':
      return {
        text: '"Through the cut, then south until the black gives up. Things on that road are mostly asleep. The yard is the part with bones. The tooth is near the warm one. Do not sort the drawers."',
        choices: [{ id: 'leave', label: 'I will not sort them.' }],
      };
    case 'drawer_got':
      return {
        text: '"That one is still proud of itself. I\'ll take the heat. You can keep the bite."',
        choices: [
          { id: 'drawturn', label: 'Pay me.' },
          { id: 'leave', label: "I'm still holding it." },
        ],
      };
    case 'drawer_done':
      return {
        text: '"That is as far as Brine goes. East of the court, past one more quiet, somebody kept a second sitting. They call it Halt. Vetch talks. Moss sells the loud toys. Your locker is still here, if your pockets get proud."',
        choices: [{ id: 'leave', label: 'Good.' }],
      };
  }
}

export function vetchLines(side: 'new' | 'coach' | 'coach_got' | 'coach_done'): Dialogue {
  switch (side) {
    case 'new':
      return {
        text: 'Vetch squints like the sun owes them money. "Halt is the sitting that is not Radville. Halfway back toward the court a coach gave up. The cushion is still in it. Bring it. Do not sit down on the way."',
        choices: [
          { id: 'coach', label: "I'll get the cushion." },
          { id: 'leave', label: 'Not halfway.' },
        ],
      };
    case 'coach':
      return {
        text: '"West, on the dull track. The coach is the one that stopped being a car. The cushion remembers a person."',
        choices: [{ id: 'leave', label: 'I can see dull.' }],
      };
    case 'coach_got':
      return {
        text: '"That is a terrible pillow. I\'ll take the memory. You can keep your back."',
        choices: [
          { id: 'coachturn', label: 'Pay me.' },
          { id: 'leave', label: "I'm still holding it." },
        ],
      };
    case 'coach_done':
      return {
        text: '"Moss has the loud toys if Peck would not. East of Halt the ground starts to shine. That is the Leak. It pays in slag and in things Peck will not stock. Do not stand in the bright, and do not wade."',
        choices: [{ id: 'leave', label: 'Good.' }],
      };
  }
}

export function mossLines(): Dialogue {
  return {
    text: 'Moss pats a tin like it might bite. "Peck sells the small opinions. I sell the ones that bloom, beam, and inhale. Tabs are tabs."',
    choices: [
      { id: 'shop', label: 'Show me the loud ones.' },
      { id: 'leave', label: 'Leave them quiet.' },
    ],
    shop: false,
  };
}

export function peckLines(): Dialogue {
  return {
    text: 'Peck clicks a claw on the counter. "I sell things that already survived somebody. The prod makes them stand still. The pop can shares the pain. White grit, three. Black drip, two. Hot slag from past Halt, five, if you lived."',
    choices: [
      { id: 'shop', label: 'Show me what survived.' },
      { id: 'leave', label: 'Leave it.' },
    ],
    shop: false,
  };
}

export function questBlurb(quest: QuestState): string {
  switch (quest) {
    case 'new':
      return 'Brine, outside the diner, has the look of a person with a job.';
    case 'accepted':
      return 'East, past the dead car. The cooler cell is in the Click Station office.';
    case 'carrying':
      return 'The cell is warm in your pocket. Bring it back to Brine.';
    case 'done':
      return 'Brine paid. The well north of the square has started keeping time.';
    case 'hum':
      return 'North, past the well. The clapper is in the stone bowl. Mind the newts.';
    case 'hum_got':
      return 'The clapper is counting in your pocket. Brine wants it.';
    case 'hum_done':
      return 'Brine heard the ground go white, east of the station.';
    case 'salt':
      return 'Past the pumps, until the dirt gives up. The keel is in the white pan.';
    case 'salt_got':
      return 'The keel is in your pocket. It wants Brine.';
    case 'salt_done':
      return 'The keel is yours. East of the pan the ground goes quiet, then the metal sings. Brine wants the hiss.';
    case 'hiss':
      return 'Out the back of the pan. The mile is a long quiet. The hiss is in the antenna court, under the mast.';
    case 'hiss_got':
      return 'The hiss is arguing in your pocket. Brine wants to hear it.';
    case 'hiss_done':
      return 'The prong is yours. South of the grease cut, past another quiet, the marrow yard has a tooth.';
    case 'drawer':
      return 'Through the cut and south. The tooth is in the marrow yard, near the warm drawer.';
    case 'drawer_got':
      return 'The tooth is warm in your pocket. Bring it to Brine.';
    case 'drawer_done':
      return 'The tooth is yours. East of the antenna court, past one more quiet, Halt is a second sitting.';
  }
}

export function handLabel(base: string | null): string {
  return base && BASES[base] ? BASES[base].name : 'fists';
}

export type StuffId = 'scrap' | 'wire' | 'rag' | 'bulb' | 'bandage' | 'stim' | 'camp' | 'grit' | 'drip' | 'slag';
export type Stacks = Partial<Record<StuffId, number>>;
export type Hotbar = (StuffId | null)[];

export type Stuff = {
  id: StuffId;
  name: string;
  blurb: string;
  sell: number;
  heal?: number;
  /** Set down from the hotbar. Not consumed by Use. */
  place?: boolean;
  /** Peck sells it, one at a time. */
  price?: number;
};

export const STACK_MAX = 50;
export const BAR_SLOTS = 6;
export const FIRE_CAP = 3;

export function emptyBar(): Hotbar {
  return Array.from({ length: BAR_SLOTS }, () => null);
}

export const STUFF: Record<StuffId, Stuff> = {
  scrap: { id: 'scrap', name: 'Bent scrap', blurb: 'Peck pays a tab. It does not go in your mouth.', sell: 1 },
  wire: { id: 'wire', name: 'Copper kink', blurb: 'Worth two tabs if it still bends.', sell: 2 },
  rag: { id: 'rag', name: 'Grease rag', blurb: 'Stops a leak. Also a person. Two of them make a bandage.', sell: 1, heal: 8 },
  bulb: { id: 'bulb', name: 'Ash bulb', blurb: 'Bitter. Closes a hole better than the rag.', sell: 1, heal: 14 },
  bandage: { id: 'bandage', name: 'Strip bandage', blurb: 'Two rags, wound tight. One pull.', sell: 2, heal: 18 },
  stim: {
    id: 'stim',
    name: 'Stim pack',
    blurb: 'A bulb, a kink of wire, and a bad idea. Puts you back on your feet.',
    sell: 4,
    heal: 26,
    price: 12,
  },
  camp: {
    id: 'camp',
    name: 'Camp tin',
    blurb: 'Sets a fire. It is not home until you say so at the coals.',
    sell: 2,
    place: true,
    price: 6,
  },
  grit: { id: 'grit', name: 'White grit', blurb: 'Peck pays three if it still sparkles. Do not put it in your eye.', sell: 3 },
  drip: { id: 'drip', name: 'Black drip', blurb: 'Peck pays two. It smells like the diner on a bad day.', sell: 2 },
  slag: { id: 'slag', name: 'Hot slag', blurb: 'Peck pays five. It is warm on purpose. Do not pocket it against your skin for long.', sell: 5 },
};

export function isStuffId(value: unknown): value is StuffId {
  return typeof value === 'string' && value in STUFF;
}

/** What a corpse leaves. Roll is 0–1. Most things leave nothing. */
export function harvestOf(kind: EnemyKind, roll: number): StuffId | null {
  if (kind === 'panback') return 'grit';
  if (kind === 'roach') return 'rag';
  if (kind === 'skip' && roll < 0.5) return 'grit';
  if (kind === 'filament') return 'bulb';
  if (kind === 'slick') return 'drip';
  if (kind === 'drifter' && roll < 0.65) return 'rag';
  if (kind === 'sleeper' && roll < 0.3) return 'drip';
  if (kind === 'mite' && roll < 0.5) return 'wire';
  if (kind === 'nail' || kind === 'mast') return 'wire';
  if (kind === 'tick' && roll < 0.6) return 'scrap';
  if (kind === 'crab' || kind === 'drawer') return 'drip';
  if (kind === 'leaker' || kind === 'count') return 'slag';
  if (kind === 'hound' && roll < 0.5) return 'rag';
  if (kind === 'walker' && roll < 0.6) return 'scrap';
  if (kind === 'moth' && roll < 0.5) return 'bulb';
  if (kind === 'clerk' || kind === 'master') return 'wire';
  if (kind === 'kettle') return 'grit';
  if (kind === 'wanderer' || kind === 'lastnumber' || kind === 'mainspring') return 'slag';
  if (kind === 'blot' && roll < 0.5) return 'drip';
  if (kind === 'surveyor') return 'wire';
  return null;
}

export type Recipe = {
  id: string;
  out: string;
  kind: 'stuff' | 'gear';
  n: number;
  need: Partial<Record<StuffId, number>>;
};

export const RECIPES: Recipe[] = [
  { id: 'bandage', out: 'bandage', kind: 'stuff', n: 1, need: { rag: 2 } },
  { id: 'stim', out: 'stim', kind: 'stuff', n: 1, need: { bulb: 1, wire: 1 } },
  { id: 'cap', out: 'cap', kind: 'gear', n: 1, need: { scrap: 3 } },
  { id: 'shirt', out: 'shirt', kind: 'gear', n: 1, need: { rag: 2 } },
  { id: 'pants', out: 'pants', kind: 'gear', n: 1, need: { rag: 2, scrap: 1 } },
];

export const MEND_R = 48;

export function inDiner(px: number, py: number): boolean {
  const tx = Math.floor(px / TILE);
  const ty = Math.floor(py / TILE);
  return tx >= 4 && tx <= 15 && ty >= NORTH + 4 && ty <= NORTH + 11;
}

/** The second sitting. Standing inside mends you. */
export function inHalt(px: number, py: number): boolean {
  const tx = Math.floor(px / TILE);
  const ty = Math.floor(py / TILE);
  return tx >= 255 && tx <= 272 && ty >= 23 && ty <= 36;
}

/** Inside Sump's walls. */
export function inSump(px: number, py: number): boolean {
  const tx = Math.floor(px / TILE);
  const ty = Math.floor(py / TILE);
  return tx >= 263 && tx <= 294 && ty >= 197 && ty <= 218;
}

/** Which town's roof you are under, if any. Roofs mend you. */
export function townAt(px: number, py: number): TownId | null {
  if (inDiner(px, py)) return 'radville';
  if (inHalt(px, py)) return 'halt';
  if (inSump(px, py)) return 'sump';
  return null;
}

export function parseBar(raw: unknown): Hotbar {
  const bar = emptyBar();
  if (!Array.isArray(raw)) return bar;
  const seen = new Set<string>();
  for (let i = 0; i < BAR_SLOTS; i++) {
    const id = raw[i];
    if (!isStuffId(id) || seen.has(id)) continue;
    seen.add(id);
    bar[i] = id;
  }
  return bar;
}

export const NODES: { id: string; stuff: StuffId; n: number; x: number; y: number }[] = [
  { id: 'scrap-square', stuff: 'scrap', n: 2, x: 10 * TILE + 8, y: town(20) * TILE + 8 },
  { id: 'scrap-road', stuff: 'scrap', n: 2, x: 32 * TILE + 8, y: town(22) * TILE + 8 },
  { id: 'wire-pad', stuff: 'wire', n: 1, x: 48 * TILE + 8, y: town(18) * TILE + 8 },
  { id: 'bulb-well', stuff: 'bulb', n: 1, x: 22 * TILE + 8, y: town(3) * TILE + 8 },
  { id: 'rag-diner', stuff: 'rag', n: 1, x: 20 * TILE + 8, y: town(14) * TILE + 8 },
  { id: 'bulb-grass', stuff: 'bulb', n: 1, x: 16 * TILE + 8, y: 8 * TILE + 8 },
  { id: 'scrap-path', stuff: 'scrap', n: 1, x: 22 * TILE + 8, y: 13 * TILE + 8 },
  { id: 'grit-pan', stuff: 'grit', n: 2, x: 84 * TILE + 8, y: 34 * TILE + 8 },
  { id: 'grit-rim', stuff: 'grit', n: 2, x: 91 * TILE + 8, y: 27 * TILE + 8 },
  { id: 'grit-door', stuff: 'grit', n: 1, x: 77 * TILE + 8, y: 36 * TILE + 8 },
  { id: 'bulb-lamps', stuff: 'bulb', n: 2, x: 6 * TILE + 8, y: 10 * TILE + 8 },
  { id: 'bulb-row', stuff: 'bulb', n: 1, x: 12 * TILE + 8, y: 8 * TILE + 8 },
  { id: 'drip-cut', stuff: 'drip', n: 2, x: 18 * TILE + 8, y: 60 * TILE + 8 },
  { id: 'drip-lip', stuff: 'drip', n: 1, x: 10 * TILE + 8, y: 55 * TILE + 8 },
  { id: 'scrap-mile', stuff: 'scrap', n: 2, x: 148 * TILE + 8, y: 31 * TILE + 8 },
  { id: 'rag-slow', stuff: 'rag', n: 1, x: 18 * TILE + 8, y: 90 * TILE + 8 },
  { id: 'wire-court', stuff: 'wire', n: 2, x: 192 * TILE + 8, y: 26 * TILE + 8 },
  { id: 'wire-dish', stuff: 'wire', n: 1, x: 206 * TILE + 8, y: 34 * TILE + 8 },
  { id: 'drip-rib', stuff: 'drip', n: 2, x: 12 * TILE + 8, y: 120 * TILE + 8 },
  { id: 'drip-yard', stuff: 'drip', n: 1, x: 30 * TILE + 8, y: 120 * TILE + 8 },
  { id: 'slag-leak', stuff: 'slag', n: 2, x: 334 * TILE + 8, y: 30 * TILE + 8 },
];

export const BOXES: { id: string; name: string; x: number; y: number; loot: Stacks; gear?: string[]; face?: 'barrel' | 'wreck'; tabs?: number }[] = [
  { id: 'locker', name: 'Back shelf', x: 8 * TILE + 8, y: town(6) * TILE + 8, loot: { scrap: 3, rag: 1, bandage: 1 } },
  { id: 'toolbox', name: 'Pump toolbox', x: 51 * TILE + 8, y: town(20) * TILE + 8, loot: { wire: 2, scrap: 1 }, gear: ['helm'] },
  { id: 'wetbox', name: 'Wet crate', x: 48 * TILE + 8, y: town(10) * TILE + 8, loot: { bulb: 2 }, gear: ['boots'] },
  { id: 'cache', name: 'Stone cache', x: 30 * TILE + 8, y: 9 * TILE + 8, loot: {}, gear: ['coat'] },
  { id: 'trunk', name: 'Bleached trunk', x: 96 * TILE + 8, y: 37 * TILE + 8, loot: { grit: 2, rag: 1 } },
  { id: 'lamptin', name: 'Lamp tin', x: 13 * TILE + 8, y: 9 * TILE + 8, loot: { bulb: 2, rag: 1 } },
  { id: 'drum', name: 'Split drum', x: 22 * TILE + 8, y: 63 * TILE + 8, loot: { drip: 3, scrap: 1 } },
  { id: 'milebox', name: 'Mile trunk', x: 154 * TILE + 8, y: 31 * TILE + 8, loot: { rag: 2, scrap: 1 }, gear: ['bell'] },
  { id: 'dishtin', name: 'Dish tin', x: 202 * TILE + 8, y: 24 * TILE + 8, loot: { wire: 3, bulb: 1 }, gear: ['needle'] },
  { id: 'bonelocker', name: 'Bone locker', x: 32 * TILE + 8, y: 122 * TILE + 8, loot: { drip: 2, scrap: 2 }, gear: ['suck'] },
  { id: 'lottin', name: 'Lot tin', x: 110 * TILE + 8, y: 118 * TILE + 8, loot: { rag: 1, stim: 1 } },
  { id: 'car-trunk', name: 'Car trunk', x: 36 * TILE + 8, y: town(22) * TILE + 8, loot: { scrap: 2, rag: 1 } },
  { id: 'road-barrel', name: 'Road barrel', x: 40 * TILE + 8, y: town(22) * TILE + 8, loot: { scrap: 2 }, face: 'barrel' },
  { id: 'north-barrel', name: 'Pale barrel', x: 70 * TILE + 8, y: 18 * TILE + 8, loot: { rag: 1, bulb: 1 }, face: 'barrel' },
  { id: 'field-car', name: 'Field car', x: 132 * TILE + 8, y: 56 * TILE + 8, loot: { wire: 1, scrap: 1 }, face: 'wreck' },
  { id: 'slow-barrel', name: 'Soft barrel', x: 18 * TILE + 8, y: 82 * TILE + 8, loot: { drip: 1, scrap: 1 }, face: 'barrel' },
  { id: 'lot-barrel', name: 'Lot barrel', x: 100 * TILE + 8, y: 118 * TILE + 8, loot: { rag: 2 }, face: 'barrel' },
  { id: 'halt-barrel', name: 'Halt barrel', x: 248 * TILE + 8, y: 31 * TILE + 8, loot: { scrap: 1, wire: 1 }, face: 'barrel' },
  { id: 'paysatchel', name: 'Pay satchel', x: 316 * TILE + 8, y: 30 * TILE + 8, loot: { slag: 2 }, tabs: 28 },
  { id: 'glowtin', name: 'Glow tin', x: 322 * TILE + 8, y: 30 * TILE + 8, loot: {}, gear: ['cowl'] },
  { id: 'seepcrate', name: 'Seep crate', x: 340 * TILE + 8, y: 31 * TILE + 8, loot: { slag: 1 }, gear: ['waders'] },
  { id: 'leadchest', name: 'Lead chest', x: 352 * TILE + 8, y: 38 * TILE + 8, loot: {}, gear: ['lead'] },
  { id: 'hotcache', name: 'Hot cache', x: 354 * TILE + 8, y: 34 * TILE + 8, loot: { slag: 3 }, gear: ['rod'] },
];

export const MARKS: { id: string; name: string; x: number; y: number }[] = [
  { id: 'diner', name: 'Radville diner', x: 18 * TILE + 8, y: town(8) * TILE + 8 },
  { id: 'peck', name: "Peck's counter", x: 21 * TILE + 8, y: town(7) * TILE + 8 },
  { id: 'well', name: 'The well', x: 25 * TILE + 8, y: town(1) * TILE + 8 },
  { id: 'station', name: 'Click Station', x: 51 * TILE + 8, y: town(9) * TILE + 8 },
  { id: 'hum', name: 'The Hum', x: 23 * TILE + 8, y: 6 * TILE + 8 },
  { id: 'salt', name: 'The white pan', x: 88 * TILE + 8, y: 28 * TILE + 8 },
  { id: 'lamps', name: 'The lamp rows', x: 8 * TILE + 8, y: 6 * TILE + 8 },
  { id: 'cut', name: 'The grease cut', x: 16 * TILE + 8, y: 56 * TILE + 8 },
  { id: 'mile', name: 'The long mile', x: 136 * TILE + 8, y: 30 * TILE + 8 },
  { id: 'platform', name: 'The platform', x: 147 * TILE + 8, y: 27 * TILE + 8 },
  { id: 'court', name: 'The antenna court', x: 204 * TILE + 8, y: 30 * TILE + 8 },
  { id: 'slow', name: 'The slow south', x: 16 * TILE + 8, y: 88 * TILE + 8 },
  { id: 'marrow', name: 'The marrow yard', x: 26 * TILE + 8, y: 121 * TILE + 8 },
  { id: 'lot', name: 'The back lot', x: 120 * TILE + 8, y: 118 * TILE + 8 },
  { id: 'halt', name: 'Halt', x: 262 * TILE + 8, y: 28 * TILE + 8 },
  { id: 'leak', name: 'The leak', x: 340 * TILE + 8, y: 30 * TILE + 8 },
  { id: 'open', name: 'The open country', x: 120 * TILE + 8, y: 12 * TILE + 8 },
  { id: 'back', name: 'The back country', x: 100 * TILE + 8, y: 76 * TILE + 8 },
  { id: 'scrub', name: 'The scrub', x: 300 * TILE + 8, y: 90 * TILE + 8 },
  { id: 'highscrub', name: 'The high scrub', x: 300 * TILE + 8, y: 8 * TILE + 8 },
  { id: 'brights', name: 'The brights', x: 410 * TILE + 8, y: 30 * TILE + 8 },
  { id: 'orchard', name: 'The glass orchard', x: 465 * TILE + 8, y: 51 * TILE + 8 },
  { id: 'flats', name: 'Kettle flats', x: 30 * TILE + 8, y: 160 * TILE + 8 },
  { id: 'kettlelair', name: 'The Kettle', x: 85 * TILE + 8, y: 230 * TILE + 8 },
  { id: 'sink', name: 'The sink', x: 240 * TILE + 8, y: 180 * TILE + 8 },
  { id: 'sump', name: 'Sump', x: 279 * TILE + 8, y: 208 * TILE + 8 },
  { id: 'dark', name: 'The dark reach', x: 440 * TILE + 8, y: 200 * TILE + 8 },
  { id: 'station', name: 'The dark station', x: 470 * TILE + 8, y: 249 * TILE + 8 },
  { id: 'undertrack', name: 'The Undertrack', x: 9 * TILE + 8, y: 267 * TILE + 8 },
  { id: 'margin', name: 'The Margin', x: EDGE.outArrive.x, y: EDGE.outArrive.y },
  { id: 'laststop', name: 'The last platform', x: LAST_STOP.platform.x, y: LAST_STOP.platform.y },
  { id: 'terminus', name: 'The Terminus', x: LAST_STOP.terminusArrive.x, y: LAST_STOP.terminusArrive.y },
];

/** Marks nobody can walk to from the diner. Jobs never send you to them. */
export const SEALED_MARKS = new Set(['undertrack', 'margin', 'laststop', 'terminus']);


export const GATHER_BACK = 40;

// ---------------------------------------------------------------------------
// Jobs. Every town keeps a board. They are small, repeatable, and they point you
// somewhere you have not been.

export type JobKind = 'cull' | 'loud' | 'look' | 'boss';
export type Job = {
  id: string;
  kind: JobKind;
  town: TownId;
  text: string;
  /** cull: enemy kind. loud: region id. look: mark id. boss: enemy kind. */
  target: string;
  need: number;
  have: number;
  tier: number;
  tabs: number;
};
export const JOB_MAX = 3;


export const tiles: number[][] = buildTiles();

/** A fingerprint of the grown map. Client and server must agree, or the walls lie. */
export const WORLD_HASH: string = (() => {
  let h = 2166136261;
  for (const row of tiles) for (const t of row) h = Math.imul(h ^ t, 16777619);
  return (h >>> 0).toString(36);
})();

/** Every pile in the world: the hand-set ones and the ones the seed grew. */
export const ALL_NODES: { id: string; stuff: StuffId; n: number; x: number; y: number }[] = [...NODES, ...GEN.nodes];

// ---------------------------------------------------------------------------
// The story. Picks up where Brine's jobs end. See docs/STORY.md (spoilers).

export type Branch = 'ride' | 'stay';

export type StoryStep = {
  kind: 'talk' | 'spot' | 'loud' | 'kill' | 'choice' | 'done';
  /** Printed as a heading in the journal where an act begins. */
  act?: string;
  /** Steps on one side of the choice. Only people who chose that side ever see them. */
  branch?: Branch;
  /** choice: the buttons. Each jumps to the first step of its branch. */
  choices?: { id: Branch; label: string }[];
  /** spot: what you find there. */
  found?: string;
  /** A relic handed over when this step is passed. */
  reward?: string;
  /** Who says the line while this step waits on them. */
  npc?: NpcId;
  /** spot: STORY_SPOTS id. loud: region id. kill: enemy kind. */
  target?: string;
  say?: string;
  /** The button that moves it along. Missing means the talk is only a reminder. */
  answer?: string;
  hint: string;
  /** What goes in the journal once you are past it. */
  learned: string;
};

export const STORY: StoryStep[] = [
  {
    kind: 'talk',
    act: 'Out of order',
    npc: 'brine',
    say: 'Brine is watching the square like it owes her. "The clapper grew back. So did the keel. Things I paid you for are growing back where you found them. That is not how things work, even here. Go look at the well. Tell me it is only a well."',
    answer: 'I will go look.',
    hint: 'Brine has something new on her mind.',
    learned: 'Things Brine paid for are growing back where they were found.',
  },
  {
    kind: 'spot',
    target: 'well',
    npc: 'brine',
    say: '"The well. North of the square. Go and look, and then come back and lie to me."',
    hint: 'Go look at the well, north of the square.',
    learned: 'Something was caught on the well lip: the clapper, or one like it, ticking backward.',
  },
  {
    kind: 'talk',
    npc: 'brine',
    say: 'Brine looks at the backward tick for a long time. Then she blinks. Both eyes, in order, for the first time since you have known her. "Oh. That is not a tick. Take it to Vetch, in Halt. Vetch reads clocks, and I do not want to."',
    answer: 'Halt, then.',
    hint: 'Brine wants Vetch, in Halt, to read the backward tick.',
    learned: 'Brine blinked in order. She did not want to read the tick herself.',
  },
  {
    kind: 'talk',
    npc: 'vetch',
    say: 'Vetch holds the tick up to one eye and does not like it. "This is not a tick. It is a schedule. Somebody is counting stops. Radville is a stop. Halt is a stop. There is a third, south, where the ground goes soft. Sump. Ora keeps the mail there. Ask her who writes to Brine."',
    answer: 'Sump.',
    hint: 'Vetch says Ora, in Sump, keeps the mail. Sump is south of Halt, down the track.',
    learned: 'The tick is a schedule. Radville, Halt and Sump are stops on something.',
  },
  {
    kind: 'talk',
    npc: 'ora',
    say: 'Ora turns a yellow envelope over with her nail. "Brine. Thirty years in the bin. I do not hand mail to strangers. Put down something loud out in the sink, and you are not a stranger."',
    answer: 'Something loud. In the sink.',
    hint: 'Ora wants something loud put down in the sink.',
    learned: 'Ora has a letter for Brine, thirty years old.',
  },
  {
    kind: 'loud',
    target: 'sink',
    npc: 'ora',
    say: '"Loud. In the sink. I will hear it from here."',
    hint: 'Put down something loud in the sink, around Sump. Loud ones hum gold.',
    learned: 'You put down something loud in the sink.',
  },
  {
    kind: 'talk',
    npc: 'ora',
    say: '"Heard it from here." Ora hands you the envelope. The stamp is a small coach with no horse. "Do not read it. It is hers."',
    answer: 'I will not read it.',
    hint: 'Ora has the letter ready for you.',
    learned: 'Ora gave you the letter. The stamp is a coach.',
  },
  {
    kind: 'talk',
    npc: 'brine',
    say: 'Brine will not touch the envelope. "Read it to me." You do. RADVILLE. STOP 3 OF 3. SERVICE ENDS WHEN THE COUNT REACHES THE LAST NUMBER. PASSENGERS REMAINING: ONE. PLEASE BE AT THE STOP. Brine is quiet a long while. "One. That was me. I was late."',
    answer: 'What count?',
    hint: 'Brine is waiting for her letter.',
    learned: 'The letter: Radville is stop 3 of 3. One passenger remains. It was Brine. She was late.',
  },
  {
    kind: 'kill',
    target: 'count',
    npc: 'brine',
    say: '"The Count. Out in the Leak. It is not a creature. It is the counter. Somebody put it down once and it got up counting from where it stopped. Put it down again. Buy me some numbers."',
    hint: 'Put down the Count, deep in the Leak. It is the counter.',
    learned: 'The Count is not a creature. It is the counter. You put it down.',
  },
  {
    kind: 'talk',
    npc: 'brine',
    say: '"Seven left," Brine says, before you tell her anything. "I can hear it from here. It was nine yesterday." She presses something into your hand: a ticket, punched once. "Keep it. The dark station runs because nobody told it the last passenger is still here. Somebody should tell it. Not today."',
    answer: 'Not today.',
    reward: 'ticket',
    hint: 'Brine will want to hear about the Count.',
    learned: 'Brine gave you her ticket. Seven left. The dark station runs because nobody told it she is still here.',
  },
  {
    kind: 'talk',
    act: 'The timetable',
    npc: 'brine',
    say: 'Brine has not moved from the step. "The cooler cell grew back too. The first thing you ever brought me. Peck says the Click Station clicks at night now, in numbers. I do not want to know which numbers. You do. Go and listen to it."',
    answer: 'I will listen.',
    hint: 'Brine wants you to listen to the Click Station, east of the square.',
    learned: 'The cooler cell grew back. The Click Station clicks at night, in numbers.',
  },
  {
    kind: 'spot',
    target: 'cooler',
    npc: 'brine',
    say: '"East. The station. Put your ear to it."',
    hint: 'Listen to the cooler cell in the Click Station.',
    learned: 'The cell clicks stop numbers: three, two, three, two. Behind it, a stair that was not there before.',
  },
  {
    kind: 'kill',
    target: 'foreman',
    npc: 'brine',
    say: '"A stair. Under my station. Under my town." Brine laughs, once, with no fun in it. "Of course there is. Go down. Whatever keeps the parts growing back is down there, and it has a boss. Everything does."',
    hint: 'Take the stair behind the cooler down into the Undertrack. Something down there runs the works.',
    learned: 'The Undertrack is the clock. The Foreman kept it wound, and you put him down.',
  },
  {
    kind: 'spot',
    target: 'works',
    hint: 'Look at the works, at the bottom of the Undertrack.',
    learned: 'Nailed to the works, a work order: REPLACE PARTS AS REMOVED AT STOP 3. DO NOT LET THE COUNT STALL. It is signed S.M.',
  },
  {
    kind: 'talk',
    npc: 'vetch',
    say: 'Vetch reads the work order twice, then a third time with the other eye. "S.M. The Stationmaster. He is not keeping the line open for her. He is keeping it on time, and a clock kept on time ends when it says it ends. He will not listen to me. He might listen to a passenger. You have a ticket."',
    answer: 'The dark station, then.',
    hint: 'Vetch has something to say about the work order.',
    learned: 'S.M. is the Stationmaster. He keeps the count on time. He might listen to a passenger with a ticket.',
  },
  {
    kind: 'kill',
    target: 'master',
    npc: 'vetch',
    say: '"East of Sump, where the country goes orderly. Take the ticket. Be early, for once."',
    hint: 'Go to the dark station with Brine\u2019s ticket, east of Sump. The Stationmaster is at the back.',
    learned: 'The Stationmaster said you were late. Late passengers are removed. It is in the rules. You were not removed.',
  },
  {
    kind: 'spot',
    target: 'safe',
    hint: 'The station safe is in the back room. What he kept is in it.',
    learned: 'In the station safe: the timetable itself. Every stop, every time. There is a stop on it between the others: two and a half.',
  },
  {
    kind: 'talk',
    npc: 'ora',
    say: 'Ora runs her nail down the timetable. "Stop two and a half. On the long mile, between the pan and the court. Nobody gets off there." She taps it. "Somebody did. Lots of somebodies. Go and see who is still standing on it."',
    answer: 'The long mile.',
    hint: 'Ora wants you to see what is at stop two and a half.',
    learned: 'Stop two and a half is on the long mile. Nobody is meant to get off there.',
  },
  {
    kind: 'talk',
    npc: 'tuck',
    say: 'A man on the platform looks up from his bench like you are the coach. "Oh. You are not it. I am Tuck. I got off early, the way everyone here did. The drifters out on the mile got off too, only they forgot why. One of us did not forget. He walks the line the wrong way, looking for the stop he missed with her. If you meet him, ask for his stub. He will not give it to you. You will have to take it."',
    answer: 'The one who walks the wrong way.',
    hint: 'Tuck, on the platform, told you about someone walking the line the wrong way.',
    learned: 'Tuck got off early. The mile drifters are passengers who forgot. One passenger did not forget.',
  },
  {
    kind: 'kill',
    target: 'wanderer',
    npc: 'tuck',
    say: '"He comes around. Everyone hears when he does. Be there when he walks by."',
    hint: 'When the Wanderer comes out, be there. Take his stub.',
    learned: 'The Wanderer had half a ticket in his coat, torn along the dotted line.',
  },
  {
    kind: 'talk',
    npc: 'brine',
    say: 'You hold out the stub. Brine does not take it. "Do not read it to me. I know whose it is. I know where it says." She closes both eyes, in order. "Keep it. The dishes in the court face inward. They always did. They were listening for the coach. When you are ready, and not before, go and help them hear it."',
    answer: 'Not before.',
    reward: 'stub',
    hint: 'Brine should see the stub.',
    learned: 'Brine knew whose stub it was without reading it. The court dishes were always listening for the coach.',
  },
  {
    kind: 'spot',
    act: 'The last coach',
    target: 'dishes',
    npc: 'brine',
    say: '"The court. The dishes. You know the way; I have paid you to go there before. Take your time. I have had thirty years of it."',
    hint: 'Go to the antenna court. The dishes are listening for something.',
    found: 'The hiss has grown back in the middle of the court, where you first found it, humming the same wrong note. You hold it up to the nearest dish. The dishes turn, all of them, slowly, from facing each other to facing west. Far past the diner, past where anything is, something whistles back.',
    learned: 'The hiss grew back. With it, the dishes turned west and heard a whistle, somewhere past the diner.',
  },
  {
    kind: 'talk',
    npc: 'brine',
    say: 'Brine puts her coat on, and then takes it off again. "West. Of course it is west. There is nothing west of the diner; that is the point of the diner." She is quiet a while. "It will not stop for a whistle. A coach stops for a flag, and only a conductor can flag it. Ours got off at the half stop with the rest of them. Before he forgot what he was for, he left me his coat. I could not look at it. I put it somewhere warm, where it would keep itself."',
    answer: 'Somewhere warm.',
    hint: 'Brine has something to say about the whistle.',
    learned: 'A coach only stops for a conductor’s flag. Brine hid the conductor’s coat somewhere warm, a long time ago.',
  },
  {
    kind: 'kill',
    target: 'drawer',
    npc: 'brine',
    say: '"The marrow yard. The drawer that stayed warm. It was keeping the coat for me, all this time. It will not want to stop."',
    hint: 'The Warm Drawer in the marrow yard has grown back. It is keeping something. Put it down.',
    learned: 'The Warm Drawer grew back, still keeping. You put it down again.',
  },
  {
    kind: 'spot',
    target: 'coat',
    reward: 'condcoat',
    hint: 'Look in what is left of the Warm Drawer.',
    found: 'In the bottom of the drawer, folded the way nobody folds anything anymore, is a coat with brass buttons and a whistle on a chain. It is still warm. It fits you, which it should not.',
    learned: 'You have the conductor’s coat. It is warm, and it fits.',
  },
  {
    kind: 'talk',
    npc: 'peck',
    say: 'Peck looks at the coat, then at the west wall, which you have never once seen him look at. "I sweep toward that wall every night. The broom comes back shorter. The dust goes over the edge and does not land." He puts the rag down. "West of the diner, the map stops. Walk off the end of the road. If she wants you to go, go. I will keep the counter."',
    answer: 'Off the end of the road.',
    hint: 'Peck, at the diner counter, has noticed the coat.',
    learned: 'Peck says the map stops west of the diner. You can walk off the end of the road.',
  },
  {
    kind: 'kill',
    target: 'surveyor',
    npc: 'peck',
    say: '"West. Off the road. Mind where the paint runs out."',
    hint: 'Walk off the west end of the road by the diner (E at the edge). Out there is the Margin, and something in it is still measuring.',
    learned: 'Past the edge is the Margin, where nobody finished painting. The Surveyor was still measuring it and did not want company. It has stopped measuring.',
  },
  {
    kind: 'spot',
    target: 'laststop',
    hint: 'At the far west end of the Margin there is a platform. Flag the coach from it.',
    found: 'The sign on the platform says RADVILLE. This is the stop, the real one. You stand at the edge in the conductor’s coat and blow the whistle, once. Far down the line, out in the paper, a light comes on and stays on. It does not move. It is waiting for a second passenger.',
    learned: 'The real Radville stop is at the end of the Margin. You flagged the coach. It is waiting for a second passenger.',
  },
  {
    kind: 'choice',
    npc: 'brine',
    say: 'Brine is standing when you come in, which she never is. "I heard it. The whole diner heard it. Peck dropped a plate." She looks at the coat on you. "So. It goes one of two ways. We ride it, you and me, all the way to wherever the line goes, and the count finishes the way it always meant to, with me on board. Or you go down under the station and break the clock for good, and the coach never comes, and I stay here and get old in order like everybody else." She shrugs. "I have wanted both for thirty years. You pick. I mean it. I will not hold it against you either way, for long."',
    choices: [
      { id: 'ride', label: 'We ride the coach.' },
      { id: 'stay', label: 'I break the clock. You stay.' },
    ],
    hint: 'Brine is waiting for you to choose.',
    learned: 'Brine let you choose.',
  },
  // ---- Ride.
  {
    kind: 'spot',
    branch: 'ride',
    target: 'coach',
    npc: 'peck',
    say: 'Peck is behind the counter, and also, somehow, in front of it. "She went on ahead. Left her apron on the hook, first time in thirty years. Go on. The coach does not wait forever, even this one."',
    hint: 'The coach is waiting at the last platform, at the west end of the Margin.',
    found: 'The coach comes out of the paper without a sound and stops exactly at the platform, early. Brine is already in a window seat with her hands in her lap. She does not look back toward the diner. The coach goes west, further west than there is, and then it stops.',
    learned: 'You rode the last coach with Brine, past the end of the Margin.',
  },
  {
    kind: 'kill',
    branch: 'ride',
    target: 'lastnumber',
    npc: 'lastbrine',
    say: 'Brine is sitting on a bench under a clock with no hands. "So this is where it goes." She does not sound disappointed. "Everybody who ever missed a coach is here somewhere, late. The count is still going, you know. It is waiting on one last number, and the number is waiting on me. Go and have a word with it. I would, but I am on holiday."',
    hint: 'Somewhere in the middle of the Terminus, the count is waiting on its last number.',
    learned: 'The Last Number was waiting in the middle of the Terminus. It is not waiting anymore.',
  },
  {
    kind: 'talk',
    branch: 'ride',
    npc: 'lastbrine',
    reward: 'punch',
    say: 'Brine listens to the quiet where the count used to be. "Huh. Nothing. I thought there would be a noise." She takes something out of her apron pocket, which is odd, since she left the apron on a hook, and gives it to you: a conductor’s punch. "Keep punching. This place goes round, in case you had not noticed. I am going to sit here a while and not be anywhere at any particular time. Come and go as you like. The coach knows the way now."',
    answer: 'I will come back.',
    hint: 'Brine is on her bench in the Terminus.',
    learned: 'The count is finished. Brine is staying at the Terminus, and she is not late for anything. The coach runs between the last platform and the Terminus now, for you.',
  },
  {
    kind: 'done',
    branch: 'ride',
    hint: 'The Terminus goes round. The coach runs from the last platform whenever you like. Sully in Sump has heard what you did. (That is the story, for now.)',
    learned: '',
  },
  // ---- Stay.
  {
    kind: 'kill',
    branch: 'stay',
    target: 'mainspring',
    npc: 'brine',
    say: '"Under the station, past the works, there is a room that ticks. Break what is in it. Do not bring me a piece. I do not want a piece."',
    hint: 'Go down into the Undertrack. Behind the works there is a room that ticks. Break the clock for good.',
    learned: 'You broke the Mainspring, in the room behind the works. The Undertrack went quiet, and so did the whistle in the west.',
  },
  {
    kind: 'talk',
    branch: 'stay',
    npc: 'brine',
    reward: 'minutehand',
    say: 'Brine is on the step. She blinks, once, and then again, in order. She waits to see if it holds. It holds. "Well." She laughs, a real one. "The glow past Halt went out at the edges this morning. Vetch sent a boy running to tell me. The coach is not coming. I am going to get old in order, and I am going to be very rude about it." She puts a clock hand in your palm, long and black. "The minute hand. It stopped on nothing. Keep it."',
    answer: 'Keep the diner open.',
    hint: 'Brine is on the step, blinking.',
    learned: 'The count stopped. Brine blinks in order now. The glow past Halt has gone out at the edges. The coach will not come, and the Terminus stays a rumor.',
  },
  {
    kind: 'done',
    branch: 'stay',
    hint: 'The count has stopped. Brine keeps the diner, in order. Sully in Sump has heard what you did. (That is the story, for now.)',
    learned: '',
  },
];

/** Where the story's "go and look" steps happen. */
export const STORY_SPOTS: Record<string, { x: number; y: number }> = {
  well: WELL,
  cooler: COOLER,
  works: { x: 51 * TILE + 8, y: 292 * TILE + 8 },
  safe: { x: 530 * TILE + 8, y: 268 * TILE + 8 },
  dishes: { x: 204 * TILE + 8, y: 30 * TILE + 8 },
  coat: { x: 18 * TILE + 8, y: 123 * TILE + 8 },
  laststop: LAST_STOP.platform,
  coach: LAST_STOP.coach,
};

/** Past this step the stair under the Click Station is open for good. */
export const STAIR_STEP = 12;

/** From this step on, the west end of the road goes somewhere. */
export const MARGIN_STEP = STORY.findIndex((step) => step.target === 'surveyor');
/** The first step of each side of the choice. */
export const BRANCH_START: Record<Branch, number> = {
  ride: STORY.findIndex((step) => step.branch === 'ride'),
  stay: STORY.findIndex((step) => step.branch === 'stay'),
};

/** Which side of the choice a character is on, if they have made it. */
export function storyBranch(story: number): Branch | null {
  return STORY[story]?.branch ?? null;
}

/** Past the ride choice's first step: Brine has left the diner, and the coach runs. */
export function rodeAway(quest: QuestState, story: number): boolean {
  return storyOpen(quest) && storyBranch(story) === 'ride';
}

/** The count is stopped for good, for this person. */
export function clockStopped(quest: QuestState, story: number): boolean {
  return storyOpen(quest) && storyBranch(story) === 'stay' && story > BRANCH_START.stay;
}

/** Whether a journal line belongs to the road this character took. */
export function storyLineFor(step: StoryStep, story: number): boolean {
  return !step.branch || step.branch === storyBranch(story);
}

/** The story only starts once Brine's own jobs are done. */
export function storyOpen(quest: QuestState): boolean {
  return quest === 'drawer_done';
}
